// src/components/Modals/ModalProgramacion/ModalProgramacion.jsx
import React, { useState, useEffect } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import axios from "axios";
import Select, { components as RSComponents } from "react-select";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../../../config/config.js";
import { toast } from "react-hot-toast";
import { HiDotsCircleHorizontal } from "react-icons/hi";
import { TbHistory } from "react-icons/tb";

import { formatIdSport } from "../../../utils/formatIdSport.js";

import ModalConfigMatch from "./ModalConfigMatch.jsx";
import ModalMatchAdvancedSettings from "./ModalMatchAdvancedSettings.jsx";
import ModalMatchOfficials from "./ModalMatchOfficials.jsx";
import ModalMatchUniformHistory from "./ModalMatchUniformHistory.jsx";
import VenueSelect from "../../Inputs/VenueSelect.jsx";
import { useUniformOptionsStatus } from "../../../hooks/useUniformOptionsStatus.js";
import { assignMatchUniforms } from "../../../services/uniformOptionsService.js";
import { getUniformErrorMessage } from "../../../admin/AdminCompany/uniformOptions.utils.js";
import MatchUniformOptionsFields, { useMatchUniformAssignment } from "./MatchUniformOptionsFields.jsx";
import {
  createHayllisV2Venue,
  listHayllisV2Venues,
} from "../../../services/hayllisV2VenueService.js";

// ───────── Caché multi-tipo (por participante y por type) ─────────
const uniformCache = new Map(); // pid -> { [type:number]: { opts: Option[], fetchedAt: number } }
const UNIF_CACHE_TTL_MS = 180_000; // 3 min (ajusta si quieres)

const isFresh = (pid, type) => {
  const slot = uniformCache.get(pid)?.[type];
  return !!slot && Date.now() - slot.fetchedAt < UNIF_CACHE_TTL_MS;
};
const getCachedOptions = (pid, type) => uniformCache.get(pid)?.[type]?.opts || [];
const setCache = (pid, type, opts) => {
  const prev = uniformCache.get(pid) || {};
  prev[type] = { opts, fetchedAt: Date.now() };
  uniformCache.set(pid, prev);
};

// ───────── Utilidades de UI ─────────
const toneTxt = (t) => (Number(t) === 1 ? "Claro" : "Oscuro");

const uniformsToOptions = (rows = [], wantedType) => {
  const seen = new Set();
  const opts = [];
  rows
    .filter((u) => Number(u.type) === Number(wantedType) && Number(u.state) === 1 && (u.color || u.color_base))
    .sort((a, b) => (a.tone || 0) - (b.tone || 0) || (a.iduniform || 0) - (b.iduniform || 0))
    .forEach((u) => {
      const colorTxt = (u.color || u.color_base || "").trim();
      if (!colorTxt) return;
      const key = `${colorTxt}__${u.tone}`;
      if (seen.has(key)) return;
      seen.add(key);
      opts.push({
        value: key,
        label: `${colorTxt} (${toneTxt(u.tone)})`,
        color: colorTxt,                 // <- este string es el que autocompleta tu input
        tone: Number(u.tone),
        code: (u.code || "").toUpperCase(), // para swatch (opcional)
      });
    });
  return opts;
};

const ColourOption = (props) => {
  const { data } = props;
  const bg = data.code && /^[0-9A-F]{6}$/i.test(data.code) ? `#${data.code}` : undefined;
  return (
    <RSComponents.Option {...props}>
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-3 w-3 rounded-full ring-1 ring-neutral-300"
          style={{ backgroundColor: bg || "transparent" }}
          title={bg || data.color}
        />
        <span>{data.label}</span>
      </div>
    </RSComponents.Option>
  );
};

const ModalProgramacion = ({ open, setOpen, match, refreshMatches }) => {
  const { idevent, idsport } = useParams();
  const [venues, setVenues] = useState([]);
  const [eventContext, setEventContext] = useState({
    idcompany: null,
    iddepartamento: null,
  });
  const [participants, setParticipants] = useState([]);
  const [formData, setFormData] = useState({
    date: match.date_match || "",
    time: match.time_match || "",
    uniforme1: match.uniforme1 || "",
    uniforme2: match.uniforme2 || "",
    uniformeArquero1: match.uniforme_exclusivo1 || "",
    uniformeArquero2: match.uniforme_exclusivo2 || "",
    uniformeMedias1: match.uniforme_medias1 || "",
    uniformeMedias2: match.uniforme_medias2 || "",
    idsede: match.sede ? match.sede.idsede : null,
    idparticipant1: match.idparticipant1 || "",
    idparticipant2: match.idparticipant2 || "",
    tag_final: match.tag_final || "",
  });
  const [newSedeModalOpen, setNewSedeModalOpen] = useState(false);
  const [newSede, setNewSede] = useState({
    name: "",
    address: "",
    linkMap: "", // Añadir este campo para el enlace de Google Maps
    iddepartamento: null,
  });

  const [openConfigMatch, setOpenConfigMatch] = useState(false);
  const [openAdvancedSettings, setOpenAdvancedSettings] = useState(false);
  const [openMatchOfficials, setOpenMatchOfficials] = useState(false);
  // FTR-0044: historial de uniformes del partido (cuarto ícono, solo con uniformes normalizados).
  const [openUniformHistory, setOpenUniformHistory] = useState(false);

  const [jerseyOptsP1, setJerseyOptsP1] = useState([]);
  const [jerseyOptsP2, setJerseyOptsP2] = useState([]);
  const [loadingUniforms, setLoadingUniforms] = useState(false);

  // Arquero (type=2)
  const [gkOptsP1, setGkOptsP1] = useState([]);
  const [gkOptsP2, setGkOptsP2] = useState([]);
  const [loadingGk, setLoadingGk] = useState(false);

  // FTR-0014: con opciones normalizadas activas, el uniforme se asigna por ID entre las opciones del equipo del
  // lado; el texto libre solo existe con el flag apagado (mientras se consulta no se envían claves de uniforme).
  const uniformOptions = useUniformOptionsStatus();
  const normalizedUniforms = !uniformOptions.loading && uniformOptions.enabled;
  const legacyUniformFields = !uniformOptions.loading && !uniformOptions.enabled;
  const matchUniforms = useMatchUniformAssignment(match.idmatch, normalizedUniforms);

  // PIDs "actuales" (si en Fase Final cambias el participante en el form, usamos el nuevo)
  const currentPid1 = formData.idparticipant1 || match.idparticipant1;
  const currentPid2 = formData.idparticipant2 || match.idparticipant2;

  // Trae en un solo request los que falten y cachea
  const ensureBatchOptionsForType = async (type) => {
    const uniqPids = [currentPid1, currentPid2]
      .filter(Boolean)
      .filter((pid, i, arr) => arr.indexOf(pid) === i);

    const need = uniqPids.filter((pid) => !isFresh(pid, type));
    if (need.length === 0) {
      // todo fresco en caché
      if (type === 1) {
        setJerseyOptsP1(getCachedOptions(currentPid1, 1));
        setJerseyOptsP2(getCachedOptions(currentPid2, 1));
      } else if (type === 2) {
        setGkOptsP1(getCachedOptions(currentPid1, 2));
        setGkOptsP2(getCachedOptions(currentPid2, 2));
      }
      return;
    }

    const setLoading = (flag) => {
      if (type === 1) setLoadingUniforms(flag);
      else if (type === 2) setLoadingGk(flag);
    };

    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/uniformsByParticipants`, {
        params: { ids: need.join(","), type, state: 1 },
      });
      // data: { [pid]: Uniform[] }
      for (const pid of need) {
        const rows = data?.[pid] || [];
        const opts = uniformsToOptions(rows, type);
        setCache(pid, type, opts);
      }

      if (type === 1) {
        setJerseyOptsP1(getCachedOptions(currentPid1, 1));
        setJerseyOptsP2(getCachedOptions(currentPid2, 1));
      } else if (type === 2) {
        setGkOptsP1(getCachedOptions(currentPid1, 2));
        setGkOptsP2(getCachedOptions(currentPid2, 2));
      }
    } catch (e) {
      console.error("Error batch uniforms type", type, e);
    } finally {
      setLoading(false);
    }
  };

  // Si cambian los participantes en el form (Fase Final), reflejar caché actual
  useEffect(() => { setJerseyOptsP1(getCachedOptions(currentPid1, 1)); }, [currentPid1]);
  useEffect(() => { setJerseyOptsP2(getCachedOptions(currentPid2, 1)); }, [currentPid2]);

  useEffect(() => { setGkOptsP1(getCachedOptions(currentPid1, 2)); }, [currentPid1]);
  useEffect(() => { setGkOptsP2(getCachedOptions(currentPid2, 2)); }, [currentPid2]);

  useEffect(() => {
    const fetchDatos = async () => {
      try {
        const eventResponse = await axios.get(
          `${API_BASE_URL}/championship/?idevent=${Number(idevent)}`
        );
        const eventData = eventResponse.data || {};
        const nextEventContext = {
          idcompany: Number(eventData.idcompany) || null,
          iddepartamento: Number(eventData.iddepartamento) || null,
        };
        setEventContext(nextEventContext);
        setNewSede(prev => ({
          ...prev,
          iddepartamento: nextEventContext.iddepartamento,
        }));

        const availableVenues = nextEventContext.idcompany
          ? await listHayllisV2Venues({
              idevent,
              idcompany: nextEventContext.idcompany,
            })
          : [];
        setVenues(availableVenues);

        if (match.sede && match.sede.idsede) {
          setFormData((prevData) => ({
            ...prevData,
            idsede: Number(match.sede.idsede),
          }));
        }

        if (match.idtypephase === 2) {
          const participantsResponse = await axios.post(
            `${API_BASE_URL}/getparticipants`,
            {
              idevent: Number(idevent),
              idsport: Number(idsport),
              idtypephase: 1,
            }
          );
          setParticipants(participantsResponse.data);
        }
      } catch (error) {
        console.error("Error fetching data", error);
      }
    };

    if (idevent) {
      fetchDatos();
    }
  }, [idevent, idsport, match.idtypephase, match.sede]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSelectChange = (selectedOption, field) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: selectedOption ? selectedOption.value : null,
    }));
  };

  const handleNewSedeChange = (e) => {
    const { name, value } = e.target;
    setNewSede((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleNewSedeSubmit = async (e) => {
    e.preventDefault();
    if (!eventContext.idcompany || !eventContext.iddepartamento) {
      toast.error("El evento no tiene compañía o departamento configurado.");
      return;
    }
    try {
      const newSedeData = await createHayllisV2Venue({
        ...newSede,
        idcompany: eventContext.idcompany,
        iddepartamento: eventContext.iddepartamento,
        idevent: Number(idevent),
      });
      setVenues((current) => [newSedeData, ...current]);
      setFormData((prevData) => ({
        ...prevData,
        idsede: Number(newSedeData.idsede || newSedeData.id),
      }));
      setNewSede({
        name: "",
        address: "",
        linkMap: "",
        iddepartamento: eventContext.iddepartamento,
      });
      setNewSedeModalOpen(false);
      toast.success("¡Nueva sede agregada exitosamente!");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error al agregar nueva sede"
      );
      console.error("Error al agregar nueva sede:", error);
    }
  };

  // En fase final, un cambio de equipos se guarda antes de asignar uniformes (las opciones son del equipo).
  const participantsChanged =
    Number(match.idtypephase) === 2 &&
    (String(formData.idparticipant1 || "") !== String(match.idparticipant1 || "") ||
      String(formData.idparticipant2 || "") !== String(match.idparticipant2 || ""));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, idmatch: match.idmatch };
    if (!legacyUniformFields) {
      // Claves omitidas = sin cambios en el backend; el uniforme va por /api/hayllis/v2/uniform-assignments.
      delete payload.uniforme1;
      delete payload.uniforme2;
      delete payload.uniformeArquero1;
      delete payload.uniformeArquero2;
    }
    try {
      console.log("form", formData)
      await axios.patch(`${API_BASE_URL}/patchprogramacionmatch`, payload);
    } catch (error) {
      // FTR-0039: si el backend explica el rechazo (p. ej. UNIFORM_TEXT_READ_ONLY), se muestra su motivo.
      toast.error(error?.response?.data?.message || "¡Error al Actualizar la Programacion!");
      console.error("Error al actualizar el partido:", error);
      return;
    }
    if (normalizedUniforms && !participantsChanged && matchUniforms.changes.length) {
      try {
        await assignMatchUniforms(match.idmatch, matchUniforms.changes);
      } catch (error) {
        toast.error(`Programación guardada, pero no los uniformes: ${getUniformErrorMessage(error)}`);
        refreshMatches();
        matchUniforms.reload();
        return;
      }
    }
    toast.success("¡Programación Actualizada Exitosamente!");
    setOpen(false);
    refreshMatches();
  };

  const sportType = formatIdSport(parseInt(idsport));
  const showUniformeArquero = sportType === 1 || sportType === 2 || sportType === 5;
  const showUniformeLibero = sportType === 3;
  const showUniformeMedias = sportType === 2 && Number(idevent) === 1;

  const handleClickMatchConfig = () => {
    setOpenConfigMatch(true);
  };

  const handleClickAdvancedSettings = () => {
    setOpenAdvancedSettings(true);
  };

  const handleClickMatchOfficials = () => {
    setOpenMatchOfficials(true);
  };

  return (
    <>
      <Modal
        aria-labelledby="modal-title"
        aria-describedby="modal-desc"
        open={open}
        onClose={() => setOpen(false)}
        className="flex justify-center items-center"
      >
        <Sheet
          variant="outlined"
          className="w-full max-w-3xl p-6 bg-white rounded-md shadow-lg"
        >
          <ModalClose
            variant="plain"
            className="m-1"
            onClick={() => setOpen(false)}
          />
          <Typography
            component="h2"
            id="modal-title"
            level="h4"
            className="text-2xl font-semibold mb-4"
          >
            {match.idgrupo
              ? `Grupo ${String.fromCharCode(64 + match.idgrupo)}: `
              : ""}
            {match.Participant1?.Institution?.abrev || "Pendiente"} vs{" "}
            {match.Participant2?.Institution?.abrev || "Pendiente"}

            <span className="ml-5 inline-flex items-center gap-2 align-middle">
              <button
                type="button"
                onClick={handleClickMatchConfig}
                className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-3 py-1.5 text-white text-sm shadow-sm hover:bg-purple-700 active:bg-purple-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2"
              >
                {/* ícono engranaje */}
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
                  <path d="M19.4 15a1.9 1.9 0 0 0 .38 2.1l.06.07a2 2 0 1 1-2.83 2.83l-.06-.07a1.9 1.9 0 0 0-2.1-.38 1.9 1.9 0 0 0-1.1 1.73V21a2 2 0 1 1-4 0v-.11a1.9 1.9 0 0 0-1.1-1.73 1.9 1.9 0 0 0-2.1.38l-.07.06A2 2 0 1 1 3.1 17.77l.07-.06a1.9 1.9 0 0 0 .38-2.1 1.9 1.9 0 0 0-1.73-1.1H1.7a2 2 0 1 1 0-4h.11a1.9 1.9 0 0 0 1.73-1.1 1.9 1.9 0 0 0-.38-2.1l-.06-.07A2 2 0 1 1 5.9 3.1l.07.06a1.9 1.9 0 0 0 2.1.38A1.9 1.9 0 0 0 9.17 1.7V1.6a2 2 0 1 1 4 0v.11a1.9 1.9 0 0 0 1.1 1.73 1.9 1.9 0 0 0 2.1-.38l.07-.06A2 2 0 1 1 20.9 6.23l-.07.06a1.9 1.9 0 0 0-.38 2.1 1.9 1.9 0 0 0 1.73 1.1h.11a2 2 0 1 1 0 4h-.11a1.9 1.9 0 0 0-1.73 1.1Z" />
                </svg>
                {/* Configurar Match */}
              </button>

              <button
                type="button"
                onClick={handleClickAdvancedSettings}
                className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-2.5 py-1 text-white text-sm shadow-sm hover:bg-purple-700 active:bg-purple-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2"
                title="Opciones avanzadas del match"
              >
                <HiDotsCircleHorizontal className="h-5 w-5" />
              </button>

              <button
                type="button"
                onClick={handleClickMatchOfficials}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-3 py-1.5 text-white text-sm shadow-sm hover:bg-emerald-700 active:bg-emerald-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2"
                title="Oficiales del partido"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M12 2 4 5v6c0 5 3.5 8.6 8 11 4.5-2.4 8-6 8-11V5l-8-3Z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
              </button>

              {normalizedUniforms && (
                <button
                  type="button"
                  onClick={() => setOpenUniformHistory(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-2.5 py-1 text-white text-sm shadow-sm hover:bg-indigo-700 active:bg-indigo-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2"
                  title="Historial de uniformes"
                  aria-label="Historial de uniformes"
                >
                  <TbHistory className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </span>

          </Typography>
          <form
            onSubmit={handleSubmit}
            className="grid gap-4 sm:grid-cols-2 mt-2"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Fecha
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>            <div>
              <label className="block text-sm font-medium text-gray-700">
                Hora
              </label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            {legacyUniformFields && (
              <>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Uniforme del Equipo 1
              </label>

              <div className="mt-1 flex flex-col sm:flex-row items-start gap-2">
                {/* Select de sugerencias */}
                <div className="w-full sm:flex-1 min-w-0">
                  <Select
                    value={null}
                    isLoading={loadingUniforms}
                    onMenuOpen={() => !loadingUniforms && ensureBatchOptionsForType(1)}
                    options={jerseyOptsP1}
                    placeholder={currentPid1 ? "Elegir uniforme" : "Sin participante"}
                    isDisabled={!currentPid1}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    components={{ Option: ColourOption }}
                    noOptionsMessage={() => (loadingUniforms ? "Cargando…" : "Sin uniformes")}
                    onChange={(opt) => {
                      if (opt?.color) setFormData((prev) => ({ ...prev, uniforme1: opt.color }));
                    }}
                    styles={{
                      control: (base) => ({ ...base, minHeight: 38 }),
                      menu: (base) => ({ ...base, maxHeight: 180, overflowY: "auto" }),
                    }}
                  />
                </div>

                {/* Input editable libremente */}
                <input
                  type="text"
                  name="uniforme1"
                  value={formData.uniforme1}
                  onChange={handleChange}
                  className="w-full sm:w-1/2 p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Uniforme del Equipo 2
              </label>

              <div className="mt-1 flex flex-col sm:flex-row items-start gap-2">
                {/* Select de sugerencias */}
                <div className="w-full sm:flex-1 min-w-0">
                  <Select
                    value={null}
                    isLoading={loadingUniforms}
                    onMenuOpen={() => !loadingUniforms && ensureBatchOptionsForType(1)}
                    options={jerseyOptsP2}
                    placeholder={currentPid2 ? "Elegir uniforme" : "Sin participante"}
                    isDisabled={!currentPid2}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    components={{ Option: ColourOption }}
                    noOptionsMessage={() => (loadingUniforms ? "Cargando…" : "Sin uniformes")}
                    onChange={(opt) => {
                      if (opt?.color) setFormData((prev) => ({ ...prev, uniforme2: opt.color }));
                    }}
                    styles={{
                      control: (base) => ({ ...base, minHeight: 38 }),
                      menu: (base) => ({ ...base, maxHeight: 180, overflowY: "auto" }),
                    }}
                  />
                </div>

                {/* Input editable libremente */}
                <input
                  type="text"
                  name="uniforme2"
                  value={formData.uniforme2}
                  onChange={handleChange}
                  className="w-full sm:w-1/2 p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>


            {showUniformeArquero && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Uniforme Arquero del Equipo 1
                  </label>

                  <div className="mt-1 flex flex-col sm:flex-row items-start gap-2">
                    {/* Select sugerencias (Arquero, type=2) */}
                    <div className="w-full sm:w-1/2 min-w-0">
                      <Select
                        value={null}
                        isLoading={loadingGk}
                        onMenuOpen={() => ensureBatchOptionsForType(2)}
                        options={gkOptsP1}
                        placeholder={currentPid1 ? "Elegir uniforme" : "Sin participante"}
                        isDisabled={!currentPid1}
                        classNamePrefix="react-select"
                        components={{ Option: ColourOption }}
                        noOptionsMessage={() => (loadingGk ? "Cargando…" : "Sin uniformes")}
                        onChange={(opt) => {
                          if (opt?.color) {
                            setFormData((prev) => ({ ...prev, uniformeArquero1: opt.color }));
                            toast.success(`Arquero 1: ${opt.color}`);
                          }
                        }}
                        styles={{
                          control: (base) => ({ ...base, minHeight: 38 }),
                          menu: (base) => ({ ...base, maxHeight: 180, overflowY: "auto" }),
                        }}
                      />
                    </div>

                    {/* Input libre */}
                    <input
                      type="text"
                      name="uniformeArquero1"
                      value={formData.uniformeArquero1}
                      onChange={handleChange}
                      className="w-full sm:w-1/2 p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Uniforme Arquero del Equipo 2
                  </label>

                  <div className="mt-1 flex flex-col sm:flex-row items-start gap-2">
                    {/* Select sugerencias (Arquero, type=2) */}
                    <div className="w-full sm:w-1/2 min-w-0">
                      <Select
                        value={null}
                        isLoading={loadingGk}
                        onMenuOpen={() => ensureBatchOptionsForType(2)}
                        options={gkOptsP2}
                        placeholder={currentPid2 ? "Elegir uniforme" : "Sin participante"}
                        isDisabled={!currentPid2}
                        classNamePrefix="react-select"
                        components={{ Option: ColourOption }}
                        noOptionsMessage={() => (loadingGk ? "Cargando…" : "Sin uniformes")}
                        onChange={(opt) => {
                          if (opt?.color) {
                            setFormData((prev) => ({ ...prev, uniformeArquero2: opt.color }));
                            toast.success(`Arquero 2: ${opt.color}`);
                          }
                        }}
                        styles={{
                          control: (base) => ({ ...base, minHeight: 38 }),
                          menu: (base) => ({ ...base, maxHeight: 180, overflowY: "auto" }),
                        }}
                      />
                    </div>

                    {/* Input libre */}
                    <input
                      type="text"
                      name="uniformeArquero2"
                      value={formData.uniformeArquero2}
                      onChange={handleChange}
                      className="w-full sm:w-1/2 p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"

                    />
                  </div>
                </div>

              </>
            )}
              </>
            )}
            {uniformOptions.loading && (
              <p className="text-sm text-gray-500 sm:col-span-2">Cargando uniformes…</p>
            )}
            {normalizedUniforms && (
              <MatchUniformOptionsFields
                state={matchUniforms}
                lockedReason={participantsChanged ? "Guarda primero el cambio de equipos; luego asigna los uniformes." : ""}
              />
            )}

            {
              showUniformeMedias && (<>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Uniforme Medias del Equipo 1
                  </label>
                  <input
                    type="text"
                    name="uniformeMedias1"
                    value={formData.uniformeMedias1}
                    onChange={handleChange}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Uniforme Medias del Equipo 2
                  </label>
                  <input
                    type="text"
                    name="uniformeMedias2"
                    value={formData.uniformeMedias2}
                    onChange={handleChange}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </>)
            }
            {legacyUniformFields && showUniformeLibero && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Uniforme Líbero del Equipo 1
                  </label>
                  <input
                    type="text"
                    name="uniformeArquero1"
                    value={formData.uniformeArquero1}
                    onChange={handleChange}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Uniforme Líbero del Equipo 2
                  </label>
                  <input
                    type="text"
                    name="uniformeArquero2"
                    value={formData.uniformeArquero2}
                    onChange={handleChange}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </>
            )}
            <div className="sm:col-span-2 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <label
                  htmlFor="programacion-venue"
                  className="block text-sm font-medium text-gray-700"
                >
                  Sede
                </label>
                <VenueSelect
                  inputId="programacion-venue"
                  venues={venues}
                  value={formData.idsede}
                  currentVenue={match.sede || null}
                  onChange={(value) =>
                    setFormData((current) => ({
                      ...current,
                      idsede: value || null,
                    }))
                  }
                  className="mt-1"
                />
              </div>
              <button
                type="button"
                onClick={() => setNewSedeModalOpen(true)}
                className="mt-1 inline-flex min-h-[42px] shrink-0 items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Nueva Sede
              </button>
            </div>
            {match.idtypephase === 2 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Participante 1
                  </label>
                  <Select
                    options={participants.map((p) => ({
                      value: p.idparticipant,
                      label: `${p.Institution.abrev} - ${p.Institution.name}`,
                    }))}
                    value={
                      formData.idparticipant1
                        ? {
                          value: formData.idparticipant1,
                          label: participants.find(
                            (p) => p.idparticipant === formData.idparticipant1
                          )?.Institution.abrev,
                        }
                        : null
                    }
                    onChange={(option) =>
                      handleSelectChange(option, "idparticipant1")
                    }
                    className="mt-1"
                    classNamePrefix="react-select"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Participante 2
                  </label>
                  <Select
                    options={participants.map((p) => ({
                      value: p.idparticipant,
                      label: `${p.Institution.abrev} - ${p.Institution.name}`,
                    }))}
                    value={
                      formData.idparticipant2
                        ? {
                          value: formData.idparticipant2,
                          label: participants.find(
                            (p) => p.idparticipant === formData.idparticipant2
                          )?.Institution.abrev,
                        }
                        : null
                    }
                    onChange={(option) =>
                      handleSelectChange(option, "idparticipant2")
                    }
                    className="mt-1"
                    classNamePrefix="react-select"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Etiqueta Final (Tag Final)
                  </label>
                  <input
                    type="text"
                    name="tag_final"
                    value={formData.tag_final}
                    onChange={handleChange}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </>
            )}

            <div className="sm:col-span-2 flex justify-end">
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Guardar
              </button>
            </div>
          </form>
        </Sheet>
      </Modal>

      <Modal
        aria-labelledby="new-sede-modal-title"
        aria-describedby="new-sede-modal-desc"
        open={newSedeModalOpen}
        onClose={() => setNewSedeModalOpen(false)}
        className="flex justify-center items-center"
      >
        <Sheet
          variant="outlined"
          className="w-full max-w-lg p-6 bg-white rounded-md shadow-lg"
        >
          <ModalClose
            variant="plain"
            className="m-1"
            onClick={() => setNewSedeModalOpen(false)}
          />
          <Typography
            component="h2"
            id="new-sede-modal-title"
            level="h4"
            className="text-2xl font-semibold mb-4"
          >
            Nueva Sede
          </Typography>
          <form onSubmit={handleNewSedeSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Sede
              </label>
              <input
                type="text"
                name="name"
                value={newSede.name}
                onChange={handleNewSedeChange}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Dirección
              </label>
              <input
                type="text"
                name="address"
                value={newSede.address}
                onChange={handleNewSedeChange}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Link del Mapa
              </label>
              <input
                type="text"
                name="linkMap"
                value={newSede.linkMap}
                onChange={handleNewSedeChange}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Guardar
              </button>
            </div>
          </form>
        </Sheet>
      </Modal>

      {openConfigMatch && (
        <ModalConfigMatch
          open={openConfigMatch}
          setOpen={setOpenConfigMatch}
          idmatch={match.idmatch}
          idevent={idevent}
          idsport={idsport}
        />
      )}

      {openAdvancedSettings && (
        <ModalMatchAdvancedSettings
          open={openAdvancedSettings}
          setOpen={setOpenAdvancedSettings}
          match={match}
          onUpdated={refreshMatches}
          onDeleted={() => setOpen(false)}
          // Con otros participantes, este modal (título y uniformes) quedaría con el par anterior.
          onParticipantsReplaced={() => setOpen(false)}
        />
      )}

      {openMatchOfficials && (
        <ModalMatchOfficials
          open={openMatchOfficials}
          setOpen={setOpenMatchOfficials}
          match={match}
        />
      )}

      {openUniformHistory && (
        <ModalMatchUniformHistory
          open={openUniformHistory}
          setOpen={setOpenUniformHistory}
          match={match}
        />
      )}
    </>
  );
};

export default ModalProgramacion;
